/**
 * Timekeeper manages timestamps for a specific context (e.g., 'producer' or
 * 'playback').
 *
 * State can optionally be backed by a `SharedArrayBuffer` (32 bytes, viewed
 * as `BigInt64Array(4)` with slots
 * `[producer:individual, producer:composite, consumer, producer:compositeLow]`).
 *
 * When backed by SAB, `setLastTimestampMicros` uses `Atomics.store` and
 * `getLastTimestampMicros` uses `Atomics.load`, so updates are visible
 * across worker boundaries without going through the main thread.
 *
 * This eliminates the per-frame Comlink `timekeeperUpdate` hop from
 * `NetworkingWorker` → main → `Timekeeper.getInstance('producer:individual')`.
 * The hop piled up during Safari main-thread stalls and amplified audio
 * latency.
 *
 * Two producer slots exist because the host runs two `StreamPacketCreator`s
 * (individual per-track and viewer-composite) which each have their own
 * `rebaseOriginWallMicros` and emit timestamps that differ by the composite
 * pipeline delay (~1.3 s in practice). Writing both into a single slot
 * caused alternating ±~1.3 s `LARGE JUMP` warnings and poisoned every
 * `timestampOffsetMicros` consumer.
 */
import { logGapDiagnosticSampled } from './GapDiagnostics';
const SLOT_PRODUCER_INDIVIDUAL = 0;
const SLOT_PRODUCER_COMPOSITE = 1;
const SLOT_CONSUMER = 2;
const SLOT_PRODUCER_COMPOSITE_LOW = 3;
const TIMEKEEPER_SAB_BYTES = 32;
const TIMEKEEPER_SLOT_COUNT = 4;
function slotForName(name) {
    if (name === 'producer:individual')
        return SLOT_PRODUCER_INDIVIDUAL;
    if (name === 'producer:composite')
        return SLOT_PRODUCER_COMPOSITE;
    if (name === 'producer:compositeLow')
        return SLOT_PRODUCER_COMPOSITE_LOW;
    if (name === 'consumer' || name === 'playback')
        return SLOT_CONSUMER;
    // Back-compat: legacy callers pass 'producer'. Map to individual slot
    // (the dominant pre-split semantics) and warn once so we can find the
    // straggler.
    if (name === 'producer') {
        warnLegacyProducerOnce();
        return SLOT_PRODUCER_INDIVIDUAL;
    }
    return SLOT_PRODUCER_INDIVIDUAL;
}
let legacyProducerWarned = false;
function warnLegacyProducerOnce() {
    if (legacyProducerWarned)
        return;
    legacyProducerWarned = true;
    console.warn("[Timekeeper] legacy 'producer' instance name used; please pass " +
        "'producer:individual' or 'producer:composite' explicitly");
}
const LARGE_JUMP_THRESHOLD_MS = 1000;
const LARGE_JUMP_LOG_INTERVAL_MS = 5000;
export class Timekeeper {
    static instances = new Map();
    static DEFAULT_INSTANCE_NAME = 'producer:individual';
    static sharedView;
    // Throttle key: `${slot}|${source}` (source defaults to 'unknown').
    static largeJumpThrottle = new Map();
    /**
     * Get a Timekeeper instance by name.
     *
     * Recognised names:
     *   - 'producer:individual' (host per-track outbound)
     *   - 'producer:composite' (host viewer-composite outbound)
     *   - 'producer:compositeLow' (host low-quality composite outbound)
     *   - 'consumer' / 'playback'
     *   - 'producer' (legacy alias for 'producer:individual')
     */
    static getInstance(name) {
        const instanceName = name ?? Timekeeper.DEFAULT_INSTANCE_NAME;
        let instance = Timekeeper.instances.get(instanceName);
        if (!instance) {
            instance = new Timekeeper(instanceName);
            Timekeeper.instances.set(instanceName, instance);
            console.log(`[Timekeeper] created instance '${instanceName}'`);
        }
        return instance;
    }
    /**
     * Bind all named instances to a shared SAB cell. Must be called early in
     * startup, before any frames flow. The same SAB must be passed to every
     * worker that touches timestamps (NetworkingWorker, encoder workers,
     * extractors).
     */
    static bindSharedSab(sab) {
        if (sab.byteLength < TIMEKEEPER_SAB_BYTES) {
            throw new Error(`Timekeeper.bindSharedSab: SAB too small (need ${TIMEKEEPER_SAB_BYTES} bytes, got ${sab.byteLength})`);
        }
        Timekeeper.sharedView = new BigInt64Array(sab, 0, TIMEKEEPER_SLOT_COUNT);
    }
    static isSabBound() {
        return Timekeeper.sharedView !== undefined;
    }
    static get SAB_BYTES() {
        return TIMEKEEPER_SAB_BYTES;
    }
    lastTimestampMicros = 0n;
    slot;
    instanceName;
    constructor(name) {
        this.instanceName = name;
        this.slot = slotForName(name);
    }
    setLastTimestampMicros(timestampMicros, source) {
        const previousValue = this.getLastTimestampMicros();
        const jump = Number(timestampMicros - previousValue) / 1000;
        const writerSource = source ?? 'unknown';
        if (Math.abs(jump) > LARGE_JUMP_THRESHOLD_MS) {
            const key = `${this.slot}|${writerSource}`;
            const now = typeof performance !== 'undefined'
                ? performance.now()
                : Date.now();
            let entry = Timekeeper.largeJumpThrottle.get(key);
            if (!entry) {
                entry = {
                    lastLogMs: Number.NEGATIVE_INFINITY,
                    suppressedCount: 0,
                    suppressedMaxAbsMs: 0,
                };
                Timekeeper.largeJumpThrottle.set(key, entry);
            }
            const sinceLast = now - entry.lastLogMs;
            if (sinceLast >= LARGE_JUMP_LOG_INTERVAL_MS) {
                console.warn(`[Timekeeper] LARGE JUMP detected: ${jump.toFixed(1)}ms`, {
                    slot: this.slot,
                    instance: this.instanceName,
                    source: writerSource,
                    previousMs: (Number(previousValue) / 1000).toFixed(1),
                    newMs: (Number(timestampMicros) / 1000).toFixed(1),
                    suppressedSinceLastLog: entry.suppressedCount,
                    suppressedMaxAbsMs: +entry.suppressedMaxAbsMs.toFixed(1),
                });
                entry.lastLogMs = now;
                entry.suppressedCount = 0;
                entry.suppressedMaxAbsMs = 0;
            }
            else {
                entry.suppressedCount += 1;
                if (Math.abs(jump) > entry.suppressedMaxAbsMs) {
                    entry.suppressedMaxAbsMs = Math.abs(jump);
                }
            }
        }
        // Sampled per-(slot, source) write rate / drift, so a future
        // dual-writer regression is visible without `LARGE JUMP` spam.
        logGapDiagnosticSampled('timekeeper.write', {
            deltaMs: Math.abs(jump),
            detail: {
                slot: this.slot,
                instance: this.instanceName,
                source: writerSource,
            },
        });
        if (Timekeeper.sharedView) {
            Atomics.store(Timekeeper.sharedView, this.slot, timestampMicros);
        }
        else {
            this.lastTimestampMicros = timestampMicros;
        }
    }
    getLastTimestampMicros() {
        if (Timekeeper.sharedView) {
            return Atomics.load(Timekeeper.sharedView, this.slot);
        }
        return this.lastTimestampMicros;
    }
}
